/* AME Parts Finder — parts-only discovery.
 *
 * ★★★ FAIL CLOSED IS THE WHOLE DESIGN. Filtering by a model NEVER falls back to a looser match.
 * If a part carries no governed fitment edge it is excluded from a model filter, full stop, even
 * when its title contains that model's name. A customer who is shown a part under their machine has
 * been told it fits; there is no honest way to say that from a string match.
 *
 * ★★★ IDENTITY COMES FROM THE INDEX, COMMERCE FROM THE DOM. Price, availability and links were
 * rendered by Liquid on this request. The JSON asset resolves identity only: manufacturer part
 * numbers (not among Shopify's searchable fields), alternate variant SKUs, and the governed
 * machine list for the equipment selector. If the index fails to load or looks stale, everything
 * degrades to the rendered fields rather than showing nothing.
 *
 * ★★★ EXACT IDENTITY FIRST, ALWAYS. If the query is an exact (or punctuation-normalized exact)
 * match on some part's OEM MPN or AME SKU, ONLY exact matches are shown — "AL123" must never
 * resolve to AL123HD, AL124 or AL123-prefixed products. This ranking outranks every sort order.
 *
 * ★★ AN EMPTY RESULT IS A RESULT. Every dead end gets specific words and a way out, because the
 * alternative — quietly widening the query until something appears — is how a parts finder sells
 * the wrong caster. An URL parameter the governed data does not recognise fails CLOSED (zero
 * results and an explanation), never open (all parts as though they all matched).
 *
 * ★ THE CORE IS PURE AND EXPOSED (window.AMEPartsCore) so the growth fixture can prove, at 500
 * synthetic parts under node, that exact identity wins and filters stay deterministic — without
 * creating 500 Shopify products.
 */
(function () {
  'use strict';

  /* ================= PURE CORE — no DOM, node-testable ================= */
  var Core = {
    norm: function (s) { return (s || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); },

    /* ★★★ EXACT IDENTITY GATE. Returns the records whose OEM MPN or any AME SKU equals the
     * query (raw, or punctuation-normalized both sides). Returns null when the query is not an
     * exact identity — partial part numbers never enter here, so a precise question can never
     * be answered with a near-miss. Ranking inside the set: raw MPN > raw SKU > normalized. */
    exactSet: function (records, q) {
      if (!q) return null;
      var Q = q.trim().toUpperCase();
      if (Q.length < 3) return null;
      var nQ = Core.norm(q);
      var hits = [];
      records.forEach(function (r) {
        var mpn = (r.mpn || '').trim().toUpperCase();
        var skus = r.skus || [];
        var tier = 0;
        if (mpn && mpn === Q) tier = 3;                     // 1. exact OEM MPN
        if (!tier) {
          for (var i = 0; i < skus.length; i++) {
            if ((skus[i] || '').trim().toUpperCase() === Q) { tier = 2; break; }  // 2. exact SKU
          }
        }
        if (!tier && nQ) {                                   // 3. normalized exact
          if (mpn && Core.norm(mpn) === nQ) tier = 1;
          else {
            for (var j = 0; j < skus.length; j++) {
              if (Core.norm(skus[j]) === nQ) { tier = 1; break; }
            }
          }
        }
        if (tier) hits.push({ r: r, tier: tier });
      });
      if (!hits.length) return null;
      hits.sort(function (a, b) { return b.tier - a.tier; });
      return hits.map(function (h) { return h.r; });
    },

    /* ★★ A MODEL CODE TYPED INTO SEARCH IS A FITMENT QUESTION. Measured on the live page:
     * searching "STA400" returned four text matches and NEITHER governed STA400 part, because
     * matching read only title/vendor/mpn/sku/category. The query is checked against the KNOWN
     * model codes (governed identities only); a hit lets the caller include verified-fit parts,
     * ranked first, and offer the one-tap governed filter. Never a substring guess — the whole
     * normalized query must equal the whole normalized code. */
    modelCodeFor: function (q, knownModels) {
      var nq = Core.norm(q);
      if (!nq || nq.length < 3) return null;
      for (var i = 0; i < (knownModels || []).length; i++) {
        if (Core.norm(knownModels[i]) === nq) return knownModels[i];
      }
      return null;
    },

    /* Relevance for NON-exact queries: 4. strong title match  5. partial textual match.
     * Partial part-number matches score like text — they are browse results, never identity.
     * mq (a validated model code the query resolved to) ranks GOVERNED fits above everything
     * except exact part identity — the fit claim itself still comes only from edges. */
    score: function (r, q, nq, mq) {
      if (!q) return 1;
      if (mq && Core.fitsModel(r, mq)) return 35;
      var Q = q.trim().toUpperCase();
      var title = (r.title || '').toUpperCase();
      if (title === Q) return 40;
      var words = title.split(/[^A-Z0-9]+/);
      for (var i = 0; i < words.length; i++) {
        if (words[i] && words[i].indexOf(Q) === 0) return 30;
      }
      if (title.indexOf(Q) !== -1) return 20;
      var hay = [r.vendor, r.mpn, (r.skus || []).join(' '), r.category].join(' ').toUpperCase();
      if (hay.indexOf(Q) !== -1) return 10;
      if (nq && nq.length >= 4) {
        if (Core.norm(r.mpn).indexOf(nq) !== -1) return 10;
        var sk = r.skus || [];
        for (var j = 0; j < sk.length; j++) {
          if (Core.norm(sk[j]).indexOf(nq) !== -1) return 10;
        }
      }
      /* ★ SEVERAL WORDS, ANY ORDER (2026-10-01). Everything above tests the query as ONE string, so "AL500 key",
       * "harmar wiring harness" and "twilight hand control" found nothing although the part exists (6 of 12
       * model + part-noun queries on live data). Lowest tier: EVERY word of a multi-word query must appear in the
       * part's own title / vendor / MPN / SKUs / category or its GOVERNED model list. All words, never "most of
       * them" and never a fuzzy spelling — an empty result stays an empty result. */
      var toks = Q.split(/[^A-Z0-9]+/).filter(function (t) { return t.length >= 2; });
      if (toks.length >= 2) {
        var all = [title, hay, (r.models || []).join(' '), (r.cmodels || []).join(' ')].join(' ').toUpperCase();
        for (var k = 0; k < toks.length; k++) { if (all.indexOf(toks[k]) === -1) return 0; }
        return 5;
      }
      return 0;
    },

    /* ★★★ GOVERNED EDGES ONLY. models/cmodels came from custom.fits_models /
     * custom.conditional_fitment; nothing else can put a record under a machine. */
    fitsModel: function (r, model) {
      return (r.models || []).indexOf(model) !== -1 || (r.cmodels || []).indexOf(model) !== -1;
    },

    /* ★★ PARAM VALIDATION FAILS CLOSED. Every filter value must exist in the governed sets the
     * page actually carries; anything else is reported invalid and the caller shows ZERO. */
    validateParams: function (state, known) {
      var bad = [];
      if (state.model && (known.models || []).indexOf(state.model) === -1) {
        bad.push({ param: 'model', value: state.model });
      }
      (state.vendors || []).forEach(function (v) {
        if ((known.vendors || []).indexOf(v) === -1) bad.push({ param: 'vendor', value: v });
      });
      (state.cats || []).forEach(function (c) {
        if ((known.cats || []).indexOf(c) === -1) bad.push({ param: 'category', value: c });
      });
      if (state.family && (known.families || []).indexOf(state.family) === -1) {
        bad.push({ param: 'family', value: state.family });
      }
      ['pmin', 'pmax'].forEach(function (k) {
        if (state[k] !== undefined && state[k] !== null && state[k] !== '') {
          var n = Number(state[k]);
          if (!isFinite(n) || n < 0) bad.push({ param: k, value: String(state[k]) });
        }
      });
      return bad;
    },

    /* ★★ DEPARTMENTS (2026-09-16, "Part store" email). Customer-facing equipment departments,
     * each a grouping of GOVERNED custom.equipment_family codes carried on the card. The order
     * here is the display order; a department with zero rendered parts is never shown. Stand-assist
     * lifts and stand aids are merchandised with patient lifts (one collection, one department). */
    DEPARTMENTS: [
      { key: 'patient-lifts', label: 'Patient Lifts & Stand Assists',
        fams: ['PATIENT_LIFT', 'STAND_ASSIST_LIFT', 'STAND_AID'] },
      { key: 'mobility-scooters', label: 'Mobility Scooters', fams: ['MOBILITY_SCOOTER'] },
      { key: 'power-wheelchairs', label: 'Power Wheelchairs', fams: ['POWER_WHEELCHAIR'] },
      { key: 'lift-chairs', label: 'Lift Chairs', fams: ['LIFT_CHAIR'] },
      { key: 'vehicle-lifts', label: 'Vehicle Lifts', fams: ['VEHICLE_LIFT'] }
    ],
    /* ★★ CATEGORY DISPLAY ALIASES (post-launch 2026-09-28). custom.part_category is free text and four values are
     * synonyms of another value. Data is NOT rewritten: each card keeps its stored value; the finder groups by the
     * canonical label and still ACCEPTS the old value as a ?category= alias (mapped before fail-closed validation),
     * so shared/legacy URLs keep working. Only true synonyms are merged (see findings.md §4). */
    CATEGORY_ALIASES: {
      'Controls and Electronics': 'Electronics and Controls',
      'Wiring and Electronics': 'Electronics and Controls',
      'Hand Controls and Remotes': 'Remotes, Switches and Controls',
      'Hardware and Mounting Components': 'Hardware and Replacement Components'
    },
    canonCategory: function (c) {
      return Object.prototype.hasOwnProperty.call(Core.CATEGORY_ALIASES, c) ? Core.CATEGORY_ALIASES[c] : c;
    },
    /* family codes -> department keys (a part proven for two families can sit in two departments) */
    deptsFor: function (families) {
      var out = [];
      Core.DEPARTMENTS.forEach(function (d) {
        for (var i = 0; i < (families || []).length; i++) {
          if (d.fams.indexOf(families[i]) !== -1) { out.push(d.key); break; }
        }
      });
      return out;
    },

    /* Deterministic filter pipeline. Returns {records, exact} — exact true when the exact
     * identity gate decided membership. Price bounds are DOLLARS; record.price is CENTS. */
    applyFilters: function (records, state) {
      var q = (state.q || '').trim();
      var nq = Core.norm(q);
      var mq = state.modelQuery || '';
      var exact = Core.exactSet(records, q);
      var base = exact || records.filter(function (r) { return Core.score(r, q, nq, mq) > 0; });
      var out = base.filter(function (r) {
        if (state.vendors && state.vendors.length &&
            state.vendors.indexOf(r.vendor) === -1) return false;
        if (state.cats && state.cats.length &&
            state.cats.indexOf(r.category) === -1) return false;
        if (state.family && (r.depts || []).indexOf(state.family) === -1) return false;
        if (state.stockOnly && !r.instock) return false;   // round 6j: confirmed stock only, not merely orderable
        if (state.model && !Core.fitsModel(r, state.model)) return false;
        var pmin = state.pmin === '' || state.pmin == null ? null : Number(state.pmin);
        var pmax = state.pmax === '' || state.pmax == null ? null : Number(state.pmax);
        if (pmin != null && isFinite(pmin) && r.price < pmin * 100) return false;
        if (pmax != null && isFinite(pmax) && pmax > 0 && r.price > pmax * 100) return false;
        return true;
      });
      return { records: out, exact: !!exact };
    },

    /* Sorting. 'relevance' = exact tier / query score, stable on original order. */
    sortRecords: function (records, mode, q, nq, mq) {
      var arr = records.slice();
      if (mode === 'az') {
        arr.sort(function (a, b) { return (a.title || '').localeCompare(b.title || ''); });
      } else if (mode === 'price-asc') {
        arr.sort(function (a, b) { return a.price - b.price || a.ord - b.ord; });
      } else if (mode === 'price-desc') {
        arr.sort(function (a, b) { return b.price - a.price || a.ord - b.ord; });
      } else {
        arr.sort(function (a, b) {
          var d = Core.score(b, q, nq, mq) - Core.score(a, q, nq, mq);
          return d !== 0 ? d : a.ord - b.ord;
        });
      }
      return arr;
    }
  };

  if (typeof window !== 'undefined') window.AMEPartsCore = Core;
  if (typeof module !== 'undefined' && module.exports) module.exports = Core;

  /* ================= PAGE WIRING ================= */
  var root = typeof document !== 'undefined' && document.querySelector &&
             document.querySelector('[data-ame-parts]');
  if (!root) return;

  var grid = root.querySelector('[data-parts-grid]');
  var cardEls = Array.prototype.slice.call(root.querySelectorAll('[data-part]'));
  var countEl = root.querySelector('[data-parts-count-live]');
  var chipsEl = root.querySelector('[data-parts-chips]');
  var emptyEl = root.querySelector('[data-parts-empty]');
  var emptyTitle = root.querySelector('[data-parts-emptytitle]');
  var emptyBody = root.querySelector('[data-parts-emptybody]');
  var qInput = root.querySelector('[data-parts-q]');
  var form = root.querySelector('[data-parts-search-form]');
  var mfrSel = root.querySelector('[data-parts-mfr]');
  var modelSel = root.querySelector('[data-parts-model]');
  var modelNote = root.querySelector('[data-parts-model-note]');
  var viewBtn = root.querySelector('[data-parts-viewparts]');
  var catChipsEl = root.querySelector('[data-parts-catchips]');
  var deptChipsEl = root.querySelector('[data-parts-deptchips]');
  var catNoteEl = root.querySelector('[data-parts-catnote]');
  var activeFamily = '';   // department key, single-select, mirrored in ?family=
  var inStock = root.querySelector('[data-parts-instock]');
  var pminEl = root.querySelector('[data-parts-pmin]');
  var pmaxEl = root.querySelector('[data-parts-pmax]');
  var sortSel = root.querySelector('[data-parts-sort]');
  var resetBtn = root.querySelector('[data-parts-reset]');
  var drawer = root.querySelector('[data-parts-filters]');
  var drawerToggle = root.querySelector('[data-parts-filtertoggle]');
  var drawerClose = root.querySelector('[data-parts-filterclose]');
  var backdrop = root.querySelector('[data-parts-backdrop]');
  var resultsEl = root.querySelector('[data-parts-results]');
  var bridgeEl = root.querySelector('[data-parts-modelbridge]');
  /* ★★ the static Liquid guard says "Showing all N parts." — TRUE only in the unfiltered state.
   * It stays for the no-JS reader and for the truncation check, and hides the moment any filter
   * or query is active, because "Showing all 118 parts" above 4 results is a contradiction. */
  var guardEl = root.querySelector('[data-parts-count][data-governed-total]');
  var drawerApply = root.querySelector('[data-parts-drawerapply]');
  var focusSearchBtn = root.querySelector('[data-parts-focus-search]');

  var invalidParams = [];   // params the governed data does not recognise → fail closed
  var index = null;
  var indexState = 'loading';

  /* records: identity + commerce snapshot parsed from the Liquid-rendered cards */
  var records = cardEls.map(function (c, i) {
    return {
      el: c, ord: i,
      handle: c.getAttribute('data-handle') || '',
      title: c.getAttribute('data-title') || '',
      vendor: c.getAttribute('data-vendor') || '',
      mpn: c.getAttribute('data-mpn') || '',
      skus: (c.getAttribute('data-sku') || '') ? [c.getAttribute('data-sku')] : [],
      category: Core.canonCategory(c.getAttribute('data-category') || ''),
      families: (c.getAttribute('data-family') || '').split(',')
        .map(function (x) { return x.trim(); }).filter(Boolean),
      models: (c.getAttribute('data-models') || '').split(',')
        .map(function (x) { return x.trim(); }).filter(Boolean),
      cmodels: (c.getAttribute('data-conditional-models') || '').split(',')
        .map(function (x) { return x.trim(); }).filter(Boolean),
      available: c.getAttribute('data-available') === 'true',
      instock: c.getAttribute('data-stock') === 'in',
      price: parseInt(c.getAttribute('data-price') || '0', 10) || 0
    };
  });
  records.forEach(function (r) { r.depts = Core.deptsFor(r.families); });

  function track(name, detail) {
    try {
      if (window.dataLayer) window.dataLayer.push(Object.assign({ event: name }, detail || {}));
      document.dispatchEvent(new CustomEvent('ame:parts:' + name, { detail: detail || {} }));
    } catch (e) { /* analytics must never break discovery */ }
  }

  /* ---- facets are built from what was actually rendered, never hardcoded ---- */
  function distinct(key, family) {
    var seen = {};
    records.forEach(function (r) {
      if (family && (r.depts || []).indexOf(family) === -1) return;
      var v = r[key]; if (v) seen[v] = (seen[v] || 0) + 1;
    });
    return Object.keys(seen).sort().map(function (k) { return { value: k, count: seen[k] }; });
  }

  function buildFacet(kind, key) {
    var host = root.querySelector('[data-parts-facet="' + kind + '"]');
    if (!host) return;
    distinct(key).forEach(function (o) {
      var id = 'ame-f-' + kind + '-' + Core.norm(o.value);
      var label = document.createElement('label');
      label.className = 'ame-parts__check';
      label.innerHTML = '<input type="checkbox" id="' + id + '" value="' +
        o.value.replace(/"/g, '&quot;') + '" data-facet="' + kind + '"> <span>' + o.value +
        ' <em>(' + o.count + ')</em></span>';
      host.appendChild(label);
    });
  }

  /* ---- verified-part counts per model come from GOVERNED edges on the rendered cards ---- */
  function modelCounts() {
    var seen = {};
    records.forEach(function (r) {
      r.models.concat(r.cmodels).forEach(function (m) { if (m) seen[m] = (seen[m] || 0) + 1; });
    });
    return seen;
  }

  /* ★★★ THE MODEL LIST COMES FROM GOVERNED MACHINE IDENTITIES, NOT FROM TITLES AND NOT ONLY
   * FROM PARTS. A supported machine with zero verified parts still appears — hiding it to make
   * coverage look better tells its owner the machine does not exist. Selecting it produces the
   * honest "still validating" state. Falls back to edge-derived models if the index is absent. */
  function buildModels() {
    var counts = modelCounts();
    var machines = (index && index.machines && index.machines.length) ? index.machines
      : Object.keys(counts).sort().map(function (c) { return { code: c, label: c, mfr: '' }; });
    var mfrs = {};
    machines.forEach(function (m) { if (m.mfr) mfrs[m.mfr] = true; });
    if (mfrSel) {
      Object.keys(mfrs).sort().forEach(function (mf) {
        var o = document.createElement('option');
        o.value = mf; o.textContent = mf;
        mfrSel.appendChild(o);
      });
    }
    function fill(filterMfr) {
      while (modelSel.options.length > 1) modelSel.remove(1);
      var groups = {};
      machines.forEach(function (m) {
        if (filterMfr && m.mfr !== filterMfr) return;
        var g = m.mfr || 'Models';
        (groups[g] = groups[g] || []).push(m);
      });
      Object.keys(groups).sort().forEach(function (g) {
        var og = document.createElement('optgroup');
        og.label = g;
        groups[g].forEach(function (m) {
          var o = document.createElement('option');
          o.value = m.code;
          var n = counts[m.code] || 0;
          var label = m.label && m.label !== m.code ? m.code + ' — ' + m.label : m.code;
          if (label.length > 58) label = label.slice(0, 55) + '…';
          o.textContent = label + (n ? '  (' + n + (n === 1 ? ' part' : ' parts') + ')' : '');
          og.appendChild(o);
        });
        modelSel.appendChild(og);
      });
    }
    fill('');
    if (mfrSel) mfrSel.addEventListener('change', function () {
      var keep = modelSel.value;
      fill(mfrSel.value);
      modelSel.value = keep;             // keep selection if it survives the narrower list
      if (modelSel.value !== keep) modelSel.value = '';
      track('parts_equipment_selected', { manufacturer: mfrSel.value || '(all)' });
      apply();
    });
    return machines.length;
  }

  /* ---- department chips: one per DEPARTMENTS entry that has rendered parts, live counts ---- */
  function deptCounts() {
    var seen = {};
    records.forEach(function (r) {
      (r.depts || []).forEach(function (k) { seen[k] = (seen[k] || 0) + 1; });
    });
    return seen;
  }
  function deptLabel(key) {
    for (var i = 0; i < Core.DEPARTMENTS.length; i++) {
      if (Core.DEPARTMENTS[i].key === key) return Core.DEPARTMENTS[i].label;
    }
    return key;
  }
  function setFamily(key) {
    activeFamily = key || '';
    invalidParams = invalidParams.filter(function (b) { return b.param !== 'family'; });
    /* a part type that does not exist in the chosen department cannot stay selected */
    var allowed = distinct('category', activeFamily).map(function (o) { return o.value; });
    Array.prototype.slice.call(root.querySelectorAll('[data-facet="category"]:checked'))
      .forEach(function (box) { if (allowed.indexOf(box.value) === -1) box.checked = false; });
    buildCategoryChips();
  }
  function buildDeptChips() {
    if (!deptChipsEl) return;
    deptChipsEl.innerHTML = '';
    var counts = deptCounts();
    var shown = Core.DEPARTMENTS.filter(function (d) { return counts[d.key] > 0; });
    if (!shown.length) { deptChipsEl.hidden = true; return; }
    var all = document.createElement('button');
    all.type = 'button';
    all.className = 'ame-parts__catchip ame-parts__catchip--dept';
    all.setAttribute('data-deptchip', '');
    all.innerHTML = 'All equipment <em>(' + records.length + ')</em>';
    all.addEventListener('click', function () { setFamily(''); apply(); });
    deptChipsEl.appendChild(all);
    shown.forEach(function (d) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ame-parts__catchip ame-parts__catchip--dept';
      b.setAttribute('data-deptchip', d.key);
      b.innerHTML = d.label + ' <em>(' + counts[d.key] + ')</em>';
      b.addEventListener('click', function () {
        setFamily(activeFamily === d.key ? '' : d.key);
        track('parts_department_selected', { department: activeFamily || '(all)' });
        apply();
      });
      deptChipsEl.appendChild(b);
    });
  }

  /* ---- shop-by-category chips: only categories with rendered parts IN THE CHOSEN DEPARTMENT,
   *      display names only, counts live ---- */
  function buildCategoryChips() {
    if (!catChipsEl) return;
    catChipsEl.innerHTML = '';
    var cats = distinct('category', activeFamily);
    if (catNoteEl) {
      catNoteEl.hidden = cats.length > 0;
      if (!cats.length) {
        catNoteEl.textContent = activeFamily
          ? 'Parts for ' + deptLabel(activeFamily) + ' are not grouped by type yet — use the list below or search by part number.'
          : 'No part types to show.';
      }
    }
    cats.forEach(function (o) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ame-parts__catchip';
      b.setAttribute('data-catchip', o.value);
      b.innerHTML = o.value + ' <em>(' + o.count + ')</em>';
      b.addEventListener('click', function () {
        var box = root.querySelector('[data-facet="category"][value="' +
          o.value.replace(/"/g, '\\"') + '"]');
        if (box) { box.checked = !box.checked; }
        track('parts_category_selected', { category: o.value, department: activeFamily || '(all)' });
        apply();
        if (resultsEl) resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      catChipsEl.appendChild(b);
    });
  }

  function selectedFacets(kind) {
    return Array.prototype.slice
      .call(root.querySelectorAll('[data-facet="' + kind + '"]:checked'))
      .map(function (i) { return i.value; });
  }

  function currentState() {
    return {
      q: (qInput.value || '').trim(),
      model: modelSel ? modelSel.value : '',
      vendors: selectedFacets('vendor'),
      cats: selectedFacets('category'),
      family: activeFamily,
      stockOnly: !!(inStock && inStock.checked),
      pmin: pminEl ? pminEl.value.trim() : '',
      pmax: pmaxEl ? pmaxEl.value.trim() : '',
      sort: sortSel ? sortSel.value : 'relevance'
    };
  }

  function apply() {
    var s = currentState();
    var visible = [];
    var mq = '';
    if (!invalidParams.length && s.q && !s.model) {
      mq = Core.modelCodeFor(s.q, knownSets().models) || '';
    }
    s.modelQuery = mq;
    if (invalidParams.length) {
      records.forEach(function (r) { r.el.hidden = true; });
    } else {
      var res = Core.applyFilters(records, s);
      var nq = Core.norm(s.q);
      var ordered = res.exact ? res.records
        : Core.sortRecords(res.records, s.sort, s.q, nq, mq);
      /* reorder the grid to the sorted order, then the hidden remainder (stable) */
      var visSet = {};
      ordered.forEach(function (r) { visSet[r.ord] = true; });
      ordered.forEach(function (r) { r.el.hidden = false; grid.appendChild(r.el); });
      records.forEach(function (r) {
        if (!visSet[r.ord]) { r.el.hidden = true; grid.appendChild(r.el); }
      });
      visible = ordered;
      /* the "Exact part-number match" prefix is true only for exact-identity results */
      records.forEach(function (r) {
        var x = r.el.querySelector('[data-fit-exact]');
        if (x) x.hidden = !res.exact;
      });
    }
    var shown = visible.length;
    if (countEl) {
      countEl.textContent = invalidParams.length ? '0 parts'
        : shown + (shown === 1 ? ' part' : ' parts');
    }
    renderChips(s);
    renderEmpty(shown, s);
    renderBridge(mq, s);
    if (guardEl) {
      guardEl.hidden = !!(invalidParams.length || s.q || s.model || s.vendors.length ||
        s.cats.length || s.family || s.stockOnly || s.pmin || s.pmax);
    }
    syncUrl(s);
    if (s.q) {
      track('parts_search', { query_length: s.q.length, results: shown });
      if (!shown) track('parts_zero_results', { query_length: s.q.length });
    }
    updateToggleBadge(s);
  }

  /* ★★ the governed one-tap bridge: "GB120 has N verified parts". Rendered only when the query
   * IS a known model code and at least one governed edge exists — the button applies the model
   * FILTER, which is the fail-closed path. Text is plain; the claim comes from edges. */
  function renderBridge(mq, s) {
    if (!bridgeEl) return;
    var n = mq ? records.filter(function (r) { return Core.fitsModel(r, mq); }).length : 0;
    if (!mq || !n) { bridgeEl.hidden = true; bridgeEl.innerHTML = ''; return; }
    bridgeEl.hidden = false;
    bridgeEl.innerHTML = '<span>' + mq + ' has ' + n + ' manufacturer-verified ' +
      (n === 1 ? 'part' : 'parts') + '.</span> ' +
      '<button type="button" class="ame-parts__bridgebtn" data-parts-bridgebtn>' +
      'Show verified parts for ' + mq + '</button>';
    var btn = bridgeEl.querySelector('[data-parts-bridgebtn]');
    if (btn) btn.addEventListener('click', function () {
      qInput.value = '';
      modelSel.value = mq;
      track('parts_model_selected', { model: mq, via: 'search_bridge' });
      apply();
      if (resultsEl) resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function activeFilterCount(s) {
    return (s.model ? 1 : 0) + s.vendors.length + s.cats.length + (s.family ? 1 : 0) +
      (s.stockOnly ? 1 : 0) + (s.pmin ? 1 : 0) + (s.pmax ? 1 : 0);
  }

  function updateToggleBadge(s) {
    if (drawerToggle) {
      var n = activeFilterCount(s);
      drawerToggle.textContent = n ? 'Filters (' + n + ')' : 'Filters';
    }
    if (drawerApply) {
      var shown = records.filter(function (r) { return !r.el.hidden; }).length;
      drawerApply.textContent = 'Show ' + shown + (shown === 1 ? ' part' : ' parts');
    }
  }

  /* ★ ACTIVE FILTERS ARE REMOVABLE WHERE THE CUSTOMER SEES THEM. A chip a customer cannot act
   * on sends them hunting through the sidebar to find which control produced it. Each chip is a
   * button that removes exactly its own filter; "Clear all" appears beside them. */
  function esc(t) {
    return String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function renderChips(s) {
    if (!chipsEl) return;
    var bits = [];
    if (s.q) bits.push({ k: 'q', v: s.q, label: '“' + esc(s.q) + '”' });
    if (s.model) bits.push({ k: 'model', v: s.model, label: esc(s.model) });
    if (s.family) bits.push({ k: 'family', v: s.family, label: esc(deptLabel(s.family)) });
    s.vendors.forEach(function (v) { bits.push({ k: 'vendor', v: v, label: esc(v) }); });
    s.cats.forEach(function (c) { bits.push({ k: 'category', v: c, label: esc(c) }); });
    if (s.stockOnly) bits.push({ k: 'instock', v: '1', label: 'In stock' });
    if (s.pmin) bits.push({ k: 'pmin', v: s.pmin, label: 'Min $' + esc(s.pmin) });
    if (s.pmax) bits.push({ k: 'pmax', v: s.pmax, label: 'Max $' + esc(s.pmax) });
    chipsEl.innerHTML = bits.map(function (b) {
      return '<button type="button" class="ame-parts__chip" data-chip-kind="' + b.k +
        '" data-chip-value="' + esc(b.v) + '" aria-label="Remove filter: ' + b.label + '">' +
        b.label + ' <span class="ame-parts__chipx" aria-hidden="true">&times;</span></button>';
    }).join('') + (bits.length > 1
      ? '<button type="button" class="ame-parts__chipclear" data-chips-clear>Clear all</button>'
      : '');
    /* department chips: exactly one is active (the "All equipment" chip when none is chosen) */
    Array.prototype.slice.call(root.querySelectorAll('[data-deptchip]')).forEach(function (c) {
      var on = (c.getAttribute('data-deptchip') || '') === (s.family || '');
      c.classList.toggle('is-active', on);
      c.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    /* category chips mirror the checkbox state so "selected" is visible where the chip lives */
    Array.prototype.slice.call(root.querySelectorAll('[data-catchip]')).forEach(function (c) {
      var on = s.cats.indexOf(c.getAttribute('data-catchip')) !== -1;
      c.classList.toggle('is-active', on);
      c.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function removeChip(kind, value) {
    if (kind === 'q') qInput.value = '';
    else if (kind === 'model') modelSel.value = '';
    else if (kind === 'family') { setFamily(''); }
    else if (kind === 'instock' && inStock) inStock.checked = false;
    else if (kind === 'pmin' && pminEl) pminEl.value = '';
    else if (kind === 'pmax' && pmaxEl) pmaxEl.value = '';
    else {
      var box = root.querySelector('[data-facet="' + kind + '"][value="' +
        value.replace(/"/g, '\\"') + '"]');
      if (box) box.checked = false;
    }
    invalidParams = invalidParams.filter(function (b) { return b.param !== kind; });
    apply();
  }

  function renderEmpty(shown, s) {
    if (shown > 0) { emptyEl.hidden = true; return; }
    emptyEl.hidden = false;
    if (invalidParams.length) {
      /* ★★★ FAIL CLOSED, AND SAY WHY. An unrecognised parameter must never widen to the whole
       * catalogue — ?model=UNKNOWN once rendered all parts as though every one fitted. */
      var p = invalidParams[0];
      emptyTitle.textContent = 'We don’t recognise “' + p.value + '”.';
      emptyBody.textContent = 'That ' + p.param + ' isn’t in our current parts data, so no parts ' +
        'are shown. Clear the filter, or search by the part number printed on the part itself.';
    } else if (s.model && !s.q) {
      emptyTitle.textContent = 'We’re still validating replacement parts for ' + s.model + '.';
      emptyBody.textContent = 'We only list a part under a model once the manufacturer confirms ' +
        'it fits. If you have the part number from the old part, search for it above — exact ' +
        'part-number matches work even before model fitment is verified.';
      track('parts_fitment_unresolved', { model: s.model });
    } else if (s.q) {
      emptyTitle.textContent = 'No matching parts found.';
      emptyBody.textContent = 'Check the part number, or browse by your equipment instead. ' +
        'Part numbers are usually printed on a label on the component itself.';
    } else {
      emptyTitle.textContent = 'No parts match these filters.';
      emptyBody.textContent = 'Try clearing a filter.';
    }
  }

  /* ---- URL state so a filtered view can be shared and survives back/refresh ---- */
  function syncUrl(s) {
    if (!window.history || !window.history.replaceState) return;
    var p = new URLSearchParams();
    if (s.q) p.set('q', s.q);
    if (s.model) p.set('model', s.model);
    if (s.family) p.set('family', s.family);
    s.vendors.forEach(function (v) { p.append('vendor', v); });
    s.cats.forEach(function (c) { p.append('category', c); });
    if (s.stockOnly) p.set('instock', '1');
    if (s.pmin) p.set('pmin', s.pmin);
    if (s.pmax) p.set('pmax', s.pmax);
    if (s.sort && s.sort !== 'relevance') p.set('sort', s.sort);
    var qs = p.toString();
    window.history.replaceState({}, '', qs ? '?' + qs : window.location.pathname);
  }

  function knownSets() {
    var models = {};
    records.forEach(function (r) {
      r.models.concat(r.cmodels).forEach(function (m) { models[m] = true; });
    });
    if (index && index.machines) {
      index.machines.forEach(function (m) { models[m.code] = true; });
    }
    return {
      models: Object.keys(models),
      vendors: distinct('vendor').map(function (o) { return o.value; }),
      cats: distinct('category').map(function (o) { return o.value; }),
      families: Object.keys(deptCounts())
    };
  }

  function restoreUrl() {
    var p = new URLSearchParams(window.location.search);
    if (p.get('q')) qInput.value = p.get('q');
    var state = {
      model: p.get('model') || '',
      family: p.get('family') || '',
      vendors: p.getAll('vendor'),
      cats: p.getAll('category').map(Core.canonCategory),
      pmin: p.get('pmin') || '',
      pmax: p.get('pmax') || ''
    };
    /* ★★★ EVERY PARAM IS VALIDATED AGAINST THE GOVERNED SETS AND FAILS CLOSED.
     * `modelSel.value = X` silently no-ops on an unknown value, which once rendered the ENTIRE
     * catalogue for ?model=AL500 — an unknown vendor or category or a non-numeric price bound
     * is the same failure wearing a different parameter name. */
    invalidParams = Core.validateParams(state, knownSets());
    if (state.model && !invalidParams.some(function (b) { return b.param === 'model'; })) {
      modelSel.value = state.model;
      if (modelSel.value !== state.model) {
        invalidParams.push({ param: 'model', value: state.model });
        modelSel.value = '';
      }
    }
    if (state.family && !invalidParams.some(function (b) { return b.param === 'family'; })) {
      setFamily(state.family);
    }
    if (p.get('instock') && inStock) inStock.checked = true;
    if (pminEl && state.pmin && !invalidParams.some(function (b) { return b.param === 'pmin'; })) {
      pminEl.value = state.pmin;
    }
    if (pmaxEl && state.pmax && !invalidParams.some(function (b) { return b.param === 'pmax'; })) {
      pmaxEl.value = state.pmax;
    }
    ['vendor', 'category'].forEach(function (kind) {
      p.getAll(kind).forEach(function (v) {
        if (kind === 'category') v = Core.canonCategory(v);
        var el = root.querySelector('[data-facet="' + kind + '"][value="' +
          v.replace(/"/g, '\\"') + '"]');
        if (el) el.checked = true;
      });
    });
    var srt = p.get('sort');
    if (sortSel && srt && ['az', 'price-asc', 'price-desc'].indexOf(srt) !== -1) {
      sortSel.value = srt;
    }
  }

  /* ---- index: identity resolution only, with a staleness signal ---- */
  function loadIndex(then) {
    var url = root.getAttribute('data-index-url');
    if (!url) { indexState = 'absent'; then(); return; }
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (j) {
        index = j;
        /* ★ a stale index must be detectable: compare its count with what was rendered. A stale
         * index still renders — identity enrichment is skipped, DOM fields carry the search. */
        indexState = Math.abs((j.count || 0) - records.length) >
          Math.max(8, records.length * 0.25) ? 'stale' : 'ok';
        if (indexState === 'ok' && j.parts) {
          var byHandle = {};
          j.parts.forEach(function (ip) { byHandle[ip.h] = ip; });
          records.forEach(function (r) {
            var ip = byHandle[r.handle];
            if (!ip) return;
            /* enrich identity only: alternate variant SKUs a card cannot carry */
            (ip.s || []).forEach(function (s) {
              if (s && r.skus.indexOf(s) === -1) r.skus.push(s);
            });
            if (!r.mpn && ip.m) r.mpn = ip.m;
          });
        } else if (indexState === 'stale') {
          try {
            console.warn('[ame-parts] index count', j.count, 'vs rendered', records.length,
              '- using rendered fields only');
          } catch (e) { /* noop */ }
        }
        then();
      })
      .catch(function () { indexState = 'error'; then(); });
  }

  /* ---- mobile filter drawer: focus managed, Esc closes, backdrop closes ---- */
  var lastFocus = null;
  function openDrawer() {
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    if (backdrop) backdrop.hidden = false;
    drawerToggle.setAttribute('aria-expanded', 'true');
    var first = drawer.querySelector('[data-parts-filterclose]') ||
      drawer.querySelector('input,select,button');
    if (first) first.focus();
    document.addEventListener('keydown', escClose);
  }
  function closeDrawer() {
    drawer.classList.remove('is-open');
    if (backdrop) backdrop.hidden = true;
    drawerToggle.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', escClose);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }
  function escClose(e) { if (e.key === 'Escape') closeDrawer(); }

  /* ================= boot ================= */
  loadIndex(function () {
    buildFacet('vendor', 'vendor');
    buildFacet('category', 'category');
    buildDeptChips();
    buildCategoryChips();
    var machineCount = buildModels();
    if (modelNote) {
      /* ★ The internal coverage statistic ("25 of 42 models…") is audit language, not customer
         language — and the block hint above the panel already says what selecting a model does.
         The note now only speaks when it has something the hint doesn't: the empty case. */
      var withParts = Object.keys(modelCounts()).length;
      if (withParts) {
        modelNote.hidden = true;
      } else {
        modelNote.hidden = false;
        modelNote.textContent = 'No verified-fit parts are listed yet — search by part number instead.';
      }
    }
    restoreUrl();
    apply();
  });

  form.addEventListener('submit', function (e) { e.preventDefault(); apply(); });
  qInput.addEventListener('input', function () {
    clearTimeout(qInput._t);
    qInput._t = setTimeout(apply, 180);
  });
  if (modelSel) modelSel.addEventListener('change', function () {
    /* a live selection is by definition a known value — clear any held invalid model */
    invalidParams = invalidParams.filter(function (b) { return b.param !== 'model'; });
    track('parts_model_selected', { model: modelSel.value || '(all)' });
    apply();
  });
  if (viewBtn) viewBtn.addEventListener('click', function () {
    apply();
    if (resultsEl) resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  if (inStock) inStock.addEventListener('change', function () {
    track('parts_filter_used', { filter: 'availability' }); apply();
  });
  [pminEl, pmaxEl].forEach(function (el) {
    if (!el) return;
    el.addEventListener('change', function () {
      invalidParams = invalidParams.filter(function (b) {
        return b.param !== 'pmin' && b.param !== 'pmax';
      });
      track('parts_filter_used', { filter: 'price' }); apply();
    });
  });
  if (sortSel) sortSel.addEventListener('change', function () {
    track('parts_filter_used', { filter: 'sort', value: sortSel.value }); apply();
  });
  root.addEventListener('change', function (e) {
    if (e.target && e.target.getAttribute('data-facet')) {
      invalidParams = invalidParams.filter(function (b) {
        return b.param !== e.target.getAttribute('data-facet');
      });
      track('parts_filter_used', { filter: e.target.getAttribute('data-facet') });
      apply();
    }
  });
  if (resetBtn) resetBtn.addEventListener('click', function () {
    invalidParams = [];
    qInput.value = '';
    setFamily('');
    if (mfrSel) mfrSel.value = '';
    modelSel.value = '';
    if (inStock) inStock.checked = false;
    if (pminEl) pminEl.value = '';
    if (pmaxEl) pmaxEl.value = '';
    if (sortSel) sortSel.value = 'relevance';
    Array.prototype.slice.call(root.querySelectorAll('[data-facet]:checked'))
      .forEach(function (i) { i.checked = false; });
    apply();
  });
  if (drawerToggle) drawerToggle.addEventListener('click', function () {
    if (drawer.classList.contains('is-open')) closeDrawer(); else openDrawer();
  });
  if (drawerApply) drawerApply.addEventListener('click', function () {
    closeDrawer();
    if (resultsEl) resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  if (chipsEl) chipsEl.addEventListener('click', function (e) {
    var clear = e.target.closest ? e.target.closest('[data-chips-clear]') : null;
    if (clear && resetBtn) { resetBtn.click(); return; }
    var chip = e.target.closest ? e.target.closest('[data-chip-kind]') : null;
    if (chip) removeChip(chip.getAttribute('data-chip-kind'), chip.getAttribute('data-chip-value') || '');
  });
  if (focusSearchBtn) focusSearchBtn.addEventListener('click', function () {
    qInput.focus();
    qInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
  if (drawerClose) drawerClose.addEventListener('click', closeDrawer);
  if (backdrop) backdrop.addEventListener('click', closeDrawer);

  /* ---- product click analytics: handle only, never customer-entered text ---- */
  grid.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('.ame-parts__cardlink') : null;
    if (a) {
      var li = a.closest('[data-part]');
      track('parts_product_clicked', { handle: li ? li.getAttribute('data-handle') : '' });
    }
  });
})();
